import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, faComments, faCubes, faServer, 
  faRobot, faEye, faEyeSlash, faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import { Line, Doughnut } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import axios from 'axios';
import { API_URL } from '../../config';

// Register Chart.js components
Chart.register(...registerables);

function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState({
    totalUsers: 0,
    totalMessages: 0,
    activeFrameworks: 0,
    activeChannels: 0,
    mcpServers: 0,
    chartData: {
      dates: [],
      messages: []
    },
    channelStats: [],
    activeUsers: [],
    openaiConfig: null
  });
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  
  useEffect(() => {
    document.title = "Admin Dashboard";
    fetchDashboardData();
  }, []);
  
  const fetchDashboardData = async () => {
    try {
      setIsLoading(true);
      setError('');
      setErrorDetails(null);
      
      // First try with API_URL/dashboard/admin
      console.log("Attempting to fetch from", `${API_URL}/dashboard/admin`);
      
      let response;
      try {
        response = await axios.get(`${API_URL}/dashboard/admin`);
      } catch (firstErr) {
        console.log("First attempt failed, trying alternative endpoint");
        
        // If first attempt fails, try with direct /dashboard/admin
        try {
          const baseUrl = API_URL.replace('/api', '');
          console.log("Attempting to fetch from", `${baseUrl}/dashboard/admin`);
          response = await axios.get(`${baseUrl}/dashboard/admin`);
        } catch (secondErr) {
          // Both attempts failed
          console.error("Both endpoint attempts failed");
          throw firstErr; // Throw the original error
        }
      }
      
      console.log("Dashboard data fetch succeeded");
      
      if (response.data.success) {
        setDashboardData(response.data);
      } else {
        setError(response.data.message || 'Failed to load dashboard data');
        setErrorDetails(response.data);
      }
    } catch (err) {
      console.error('Error fetching admin dashboard data:', err);
      setError('Failed to load dashboard data. Please check your network connection.');
      setErrorDetails({
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        url: err.config?.url,
        data: err.response?.data,
        stack: err.stack
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  // Helper function to generate last 7 days
  const generateLastSevenDays = () => {
    const dates = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(d.toISOString().split('T')[0]);
    }
    return dates;
  };
  
  // Message chart configuration
  const messageChartData = {
    labels: dashboardData.chartData?.dates || [],
    datasets: [
      {
        label: 'Messages',
        data: dashboardData.chartData?.messages || [],
        borderColor: 'rgba(78, 115, 223, 1)',
        backgroundColor: 'rgba(78, 115, 223, 0.05)',
        borderWidth: 2,
        tension: 0.3,
        fill: true
      }
    ]
  };
  
  // Channel distribution chart
  const channelColors = ['#25D366', '#0088cc', '#4e73df', '#1cc88a'];
  const channelNames = dashboardData.channelStats?.map(stat => stat.channel_name) || [];
  const channelCounts = dashboardData.channelStats?.map(stat => stat.count) || [];
  
  const channelChartData = {
    labels: channelNames,
    datasets: [
      {
        data: channelCounts,
        backgroundColor: channelColors.slice(0, channelNames.length),
        hoverBackgroundColor: channelColors.slice(0, channelNames.length),
        hoverBorderColor: 'rgba(234, 236, 244, 1)',
      }
    ]
  };
  
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger my-3">
        <FontAwesomeIcon icon={faExclamationCircle} className="me-2" />
        <strong>Error:</strong> {error}
        {errorDetails && (
          <details style={{ whiteSpace: 'pre-wrap', marginTop: 10 }}>
            <summary>Show error details</summary>
            {typeof errorDetails === 'object'
              ? JSON.stringify(errorDetails, null, 2)
              : String(errorDetails)}
          </details>
        )}
        <div className="mt-3">
          <button 
            className="btn btn-primary btn-sm" 
            onClick={fetchDashboardData}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div>
      {/* Stats Cards */}
      <Row className="mb-4">
        <Col xl={3} md={6} className="mb-3">
          <Card className="border-left-primary shadow h-100">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-xs font-weight-bold text-primary text-uppercase mb-1">
                    Total Users
                  </div>
                  <div className="h5 mb-0 font-weight-bold">{dashboardData.totalUsers}</div>
                </div>
                <div>
                  <FontAwesomeIcon icon={faUsers} className="fa-2x text-gray-300" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xl={3} md={6} className="mb-3">
          <Card className="border-left-success shadow h-100">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Total Messages
                  </div>
                  <div className="h5 mb-0 font-weight-bold">{dashboardData.totalMessages}</div>
                </div>
                <div>
                  <FontAwesomeIcon icon={faComments} className="fa-2x text-gray-300" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xl={3} md={6} className="mb-3">
          <Card className="border-left-info shadow h-100">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                    Frameworks / Channels
                  </div>
                  <div className="h5 mb-0 font-weight-bold">
                    {dashboardData.activeFrameworks} / {dashboardData.activeChannels}
                  </div>
                </div>
                <div>
                  <FontAwesomeIcon icon={faCubes} className="fa-2x text-gray-300" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        <Col xl={3} md={6} className="mb-3">
          <Card className="border-left-warning shadow h-100">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <div className="text-xs font-weight-bold text-warning text-uppercase mb-1">
                    MCP Servers
                  </div>
                  <div className="h5 mb-0 font-weight-bold">{dashboardData.mcpServers}</div>
                </div>
                <div>
                  <FontAwesomeIcon icon={faServer} className="fa-2x text-gray-300" />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      <Row>
        {/* Message Chart */}
        <Col xl={8} lg={7} className="mb-4">
          <Card className="shadow">
            <Card.Header className="py-3">
              <h6 className="m-0 font-weight-bold">Message Activity (Last 7 Days)</h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '300px' }}>
                <Line 
                  data={messageChartData} 
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    scales: {
                      y: {
                        beginAtZero: true,
                        ticks: {
                          precision: 0
                        }
                      }
                    }
                  }} 
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        {/* Channel Distribution */}
        <Col xl={4} lg={5} className="mb-4">
          <Card className="shadow">
            <Card.Header className="py-3">
              <h6 className="m-0 font-weight-bold">Messages by Channel</h6>
            </Card.Header>
            <Card.Body>
              <div style={{ height: '260px', display: 'flex', justifyContent: 'center' }}>
                <Doughnut 
                  data={channelChartData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    cutout: '70%',
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }}
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
        
        {/* OpenAI Configuration */}
        <Col xl={4} lg={6} className="mb-4">
          <Card className="shadow">
            <Card.Header>
              <h6 className="m-0 font-weight-bold">OpenAI Configuration</h6>
            </Card.Header>
            <Card.Body>
              {dashboardData.openaiConfig ? (
                <>
                  <div className="mb-3">
                    <h6>Model: <span className="text-primary">{dashboardData.openaiConfig.model}</span></h6>
                  </div>
                  <div className="mb-3">
                    <h6>Temperature: <span className="text-primary">{dashboardData.openaiConfig.temperature}</span></h6>
                  </div>
                  <div className="mb-3">
                    <h6>API Key:</h6>
                    <div className="input-group">
                      <input 
                        type={showApiKey ? "text" : "password"} 
                        className="form-control bg-light border-0" 
                        value={dashboardData.openaiConfig.api_key} 
                        readOnly
                      />
                      <button 
                        className="btn btn-primary" 
                        type="button" 
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        <FontAwesomeIcon icon={showApiKey ? faEyeSlash : faEye} />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center p-4">
                  <FontAwesomeIcon icon={faRobot} className="text-warning mb-3" style={{ fontSize: '2rem' }} />
                  <p>OpenAI not configured!</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
        
        {/* Most Active Users */}
        <Col xl={8} lg={6} className="mb-4">
          <Card className="shadow">
            <Card.Header>
              <h6 className="m-0 font-weight-bold">Most Active Users</h6>
            </Card.Header>
            <Card.Body>
              {dashboardData.activeUsers?.length > 0 ? (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>User Name</th>
                      <th>Message Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dashboardData.activeUsers.map((user, index) => (
                      <tr key={index}>
                        <td>{user.user_name}</td>
                        <td>{user.message_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <div className="text-center p-4">No active users data available</div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default AdminDashboard;
